import React, { useContext } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  Image,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

export type ExistingProfileDialogProps = {
  open: boolean;
  // The inviting customer's name, shown inline in the body copy. Falls back to a generic
  // phrase when unavailable.
  customerName?: string | null;
  // Disable both actions while the activate request triggered by "Use existing profile" runs.
  isBusy?: boolean;
  onUseExistingProfile: () => void;
  onUseDifferentEmail: () => void;
};

// Shown when the user picked "Create an account" pre-auth but ActivationOptions reveals the
// signed-in identity already maps to an existing Microsoft Advertising user profile (the
// skip-form / consent-only path). Offers to either reuse that profile to accept the invitation,
// or sign out and start over with a different email.
export const ExistingProfileDialog: React.FC<ExistingProfileDialogProps> = ({
  open,
  customerName,
  isBusy,
  onUseExistingProfile,
  onUseDifferentEmail,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const title = i18n.getString(_TL_('An existing profile was found'));
  const body = customerName
    ? i18n.getString(
      _TL_('This email is already linked to a Microsoft Advertising user profile. To join {{customerName}}, use your existing profile. If you want to keep this access in a separate profile, go back and create a new one using a different email.'),
      { customerName }
    )
    : i18n.getString(_TL_('This email is already linked to a Microsoft Advertising user profile. To join this account, use your existing profile. If you want to keep this access in a separate profile, go back and create a new one using a different email.'));

  return (
    <Dialog open={open} modalType="alert">
      <DialogSurface
        className={classes.dialogSurface}
        aria-labelledby="existing-profile-dialog-title"
        aria-describedby="existing-profile-dialog-description"
      >
        <DialogBody className="existing-profile-dialog">
          <DialogContent className={classes.dialogContent}>
            <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />
            <Title3 id="existing-profile-dialog-title" className={classes.dialogTitle}>{title}</Title3>
            <Text id="existing-profile-dialog-description" block>{body}</Text>
            <div className={classes.dialogActions}>
              <Button
                className="existing-profile-use-existing"
                appearance="primary"
                disabled={isBusy}
                onClick={onUseExistingProfile}
              >
                {i18n.getString(_TL_('Use existing profile'))}
              </Button>
              <Button
                className="existing-profile-use-different-email"
                appearance="secondary"
                disabled={isBusy}
                onClick={onUseDifferentEmail}
              >
                {i18n.getString(_TL_('Use a different email'))}
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
